import React from 'react';
import { alpha, Checkbox as CheckboxMUI, FormControlLabel, styled } from '@mui/material';
import { SxProps, Theme } from '@mui/system';

interface SelectAllCheckboxProps {
  checked: boolean;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  label?: string;
  labelPlacement?: 'start' | 'end';
  sx?: SxProps<Theme>;
  indeterminate?: boolean;
  /**
   * Subtle styling: thin, light-primary border when unchecked; solid primary
   * fill when checked/indeterminate. Used where the checkbox should stay quiet
   * until selected (e.g. bulk-select rows).
   */
  subtle?: boolean;
}

// Thin, low-emphasis box shown when the subtle checkbox is unchecked. Uses a
// neutral divider border (not primary) so it stays very quiet until hovered.
const SubtleIcon = styled('span')(({ theme }) => ({
  width: 16,
  height: 16,
  borderRadius: 4,
  backgroundColor: 'transparent',
  boxShadow: `inset 0 0 0 1px ${theme.palette.divider}`,
  'input:hover ~ &': {
    boxShadow: `inset 0 0 0 1px ${alpha(theme.palette.primary.main, 0.6)}`,
  },
  'input:disabled ~ &': {
    boxShadow: `inset 0 0 0 1px ${theme.palette.action.disabledBackground}`,
  },
}));

// Solid primary box (with the glyph drawn via ::before) for the selected state.
const SubtleFilledIcon = styled(SubtleIcon)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  boxShadow: `inset 0 0 0 1px ${theme.palette.primary.main}`,
  '&::before': {
    display: 'block',
    width: 16,
    height: 16,
    content: '""',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
    backgroundSize: '11px 11px',
  },
}));

const SubtleCheckedIcon = styled(SubtleFilledIcon)({
  '&::before': {
    backgroundImage:
      "url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23fff'%3E%3Cpath d='M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z'/%3E%3C/svg%3E\")",
  },
});

const SubtleIndeterminateIcon = styled(SubtleFilledIcon)({
  '&::before': {
    backgroundImage:
      "url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23fff'%3E%3Crect x='6' y='11' width='12' height='2' rx='1'/%3E%3C/svg%3E\")",
  },
});

export const Checkbox: React.FC<SelectAllCheckboxProps> = ({
  checked,
  onChange,
  label,
  labelPlacement = 'start',
  sx,
  indeterminate,
  subtle,
}) => {
  const handleChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onChange(event);
    },
    [onChange],
  );

  const subtleIconProps = subtle
    ? {
        icon: <SubtleIcon />,
        checkedIcon: <SubtleCheckedIcon />,
        indeterminateIcon: <SubtleIndeterminateIcon />,
        disableRipple: true,
      }
    : {};

  return (
    <FormControlLabel
      sx={sx}
      label={label}
      labelPlacement={labelPlacement}
      control={
        <CheckboxMUI
          checked={checked}
          indeterminate={indeterminate}
          onChange={handleChange}
          onClick={(event) => event.stopPropagation()}
          color="primary"
          {...subtleIconProps}
        />
      }
    />
  );
};

export default React.memo(Checkbox);
